/**
 * useResponsive Hook
 * 
 * A comprehensive responsive design hook that provides:
 * - Screen size detection (mobile, tablet, desktop, large desktop)
 * - Orientation detection (portrait/landscape)
 * - Touch device detection
 * - Dynamic breakpoint matching
 * - Debounced resize handling for performance
 * 
 * Usage:
 *   const { isMobile, isTablet, isDesktop, orientation, isTouchDevice } = useResponsive();
 *   
 *   // Conditional rendering
 *   if (isMobile) return <MobileLayout />;
 *   
 *   // Conditional styling
 *   <div style={{ padding: isMobile ? '8px' : '24px' }}>
 * 
 * Created: December 2024
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

// Standard breakpoints (following Material Design / Bootstrap conventions)
export const BREAKPOINTS = {
  xs: 0,      // Extra small: phones (portrait)
  sm: 576,    // Small: phones (landscape)
  md: 768,    // Medium: tablets
  lg: 992,    // Large: desktops
  xl: 1200,   // Extra large: large desktops
  xxl: 1400,  // Extra extra large: wide monitors
};

// Debounce utility for performance
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Main responsive hook
 */
export const useResponsive = () => {
  // Get initial values (SSR-safe)
  const getWindowDimensions = useCallback(() => {
    if (typeof window === 'undefined') {
      return { width: 1200, height: 800 };
    }
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }, []);

  const [dimensions, setDimensions] = useState(getWindowDimensions);

  // Debounced resize handler for performance
  useEffect(() => {
    const handleResize = debounce(() => {
      setDimensions(getWindowDimensions());
    }, 100); // 100ms debounce

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Initial call to ensure correct values
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [getWindowDimensions]);

  // Memoized computed values for performance
  const responsive = useMemo(() => {
    const { width, height } = dimensions;

    // Screen size booleans
    const isXs = width < BREAKPOINTS.sm;
    const isSm = width >= BREAKPOINTS.sm && width < BREAKPOINTS.md;
    const isMd = width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;
    const isLg = width >= BREAKPOINTS.lg && width < BREAKPOINTS.xl;
    const isXl = width >= BREAKPOINTS.xl && width < BREAKPOINTS.xxl;
    const isXxl = width >= BREAKPOINTS.xxl;

    // Convenient aliases
    const isMobile = width < BREAKPOINTS.md;           // < 768px
    const isTablet = width >= BREAKPOINTS.md && width < BREAKPOINTS.lg; // 768-991px
    const isDesktop = width >= BREAKPOINTS.lg;         // >= 992px
    const isLargeDesktop = width >= BREAKPOINTS.xl;    // >= 1200px
    const isWideScreen = width >= BREAKPOINTS.xxl;     // >= 1400px

    // Orientation
    const isPortrait = height > width;
    const isLandscape = width >= height;
    const orientation = isPortrait ? 'portrait' : 'landscape';

    // Touch device detection
    const isTouchDevice = typeof window !== 'undefined' && (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );

    // Current breakpoint name
    let breakpoint = 'xs';
    if (isXxl) breakpoint = 'xxl';
    else if (isXl) breakpoint = 'xl';
    else if (isLg) breakpoint = 'lg';
    else if (isMd) breakpoint = 'md';
    else if (isSm) breakpoint = 'sm';

    return {
      // Dimensions
      width,
      height,
      
      // Exact breakpoints
      isXs,
      isSm,
      isMd,
      isLg,
      isXl,
      isXxl,
      
      // Convenient aliases
      isMobile,
      isTablet,
      isDesktop,
      isLargeDesktop,
      isWideScreen,
      
      // Orientation
      isPortrait,
      isLandscape,
      orientation,
      
      // Device type
      isTouchDevice,
      
      // Current breakpoint
      breakpoint,
      
      // Helper functions
      up: (bp) => width >= BREAKPOINTS[bp],      // Match this breakpoint and up
      down: (bp) => width < BREAKPOINTS[bp],     // Match below this breakpoint
      only: (bp) => breakpoint === bp,           // Match only this breakpoint
      between: (min, max) => width >= BREAKPOINTS[min] && width < BREAKPOINTS[max],
    };
  }, [dimensions]);

  return responsive;
};

/**
 * Hook for responsive values - returns different values based on screen size
 * 
 * Usage:
 *   const padding = useResponsiveValue({ mobile: 8, tablet: 16, desktop: 24 });
 *   const columns = useResponsiveValue({ mobile: 1, tablet: 2, desktop: 4 });
 */
export const useResponsiveValue = (values) => {
  const { isMobile, isTablet } = useResponsive();
  
  if (isMobile && values.mobile !== undefined) return values.mobile;
  if (isTablet && values.tablet !== undefined) return values.tablet;
  return values.desktop;
};

/**
 * Hook for responsive styles - merges base styles with responsive overrides
 * 
 * Usage:
 *   const styles = useResponsiveStyles(
 *     { padding: 24, fontSize: 16 },  // base
 *     { mobile: { padding: 8, fontSize: 14 }, tablet: { padding: 16 } }  // overrides
 *   );
 */
export const useResponsiveStyles = (baseStyles, responsiveOverrides = {}) => {
  const { isMobile, isTablet } = useResponsive();
  
  let overrides = {};
  if (isMobile && responsiveOverrides.mobile) {
    overrides = responsiveOverrides.mobile;
  } else if (isTablet && responsiveOverrides.tablet) {
    overrides = responsiveOverrides.tablet;
  }
  
  return { ...baseStyles, ...overrides };
};

export default useResponsive;

